import React, { useState, useEffect } from "react";
import {
  Box,
  Center,
  VStack,
  FormControl,
  FormLabel,
  FormErrorMessage,
  HStack,
  PinInput,
  PinInputField,
  Text,
  Spinner,
  useToast,
  Button,
} from "@chakra-ui/react";
import { useLocation, useNavigate } from "react-router-dom";
import CustomButton from "../../components/UI/CustomButton";
import { useFormTheme } from "../../hooks/useFormTheme";
import AuthLogo from "../../components/UI/AuthLogo";
import { useAuth } from "../../context/AuthContext";
import { USER_EMAIL_KEY } from "../../helpers/constant";
import FormErrorBox from "../../components/UI/FormErrorBox";
import { useTranslation } from "react-i18next";
import { getLocalizedAuthErrorMessage } from "../../helpers/authErrors";
import { motion } from "framer-motion";

const MotionBox = motion(Box);

const authCardTransition = {
  layout: { duration: 0.2, ease: [0.22, 1, 0.36, 1] },
};

interface VerifyCodeLocationState {
  email?: string;
}

const VerifyCodePage: React.FC = () => {
  const [code, setCode] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const navigate = useNavigate();
  const location = useLocation();
  const toast = useToast();
  const { t } = useTranslation();
  const ts = (key: string): string => String(t(key));
  const { verifyCodeHandler, resendVerificationCodeHandler, isLoading } =
    useAuth();
  const persistedEmail = localStorage.getItem(USER_EMAIL_KEY);
  const state = (location.state as VerifyCodeLocationState | null) ?? null;
  const emailFromState = state?.email ?? "";
  const targetEmail = emailFromState || persistedEmail || "";
  const {
    bg,
    errorColor,
    linkColor,
    pageBg,
    textColor,
    lightGradient,
    darkGradient,
  } = useFormTheme();

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((prev) => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (code.length !== 6) {
      setError(ts("auth.verify.invalidCode"));
      return;
    }

    if (!targetEmail) {
      setError(ts("auth.verify.missingEmail"));
      return;
    }

    setError("");
    setLoading(true);
    try {
      await verifyCodeHandler(targetEmail, code);

      localStorage.removeItem(USER_EMAIL_KEY);

      toast({
        title: t("auth.verify.successTitle"),
        description: t("auth.verify.successDescription"),
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });

      navigate("/login");
    } catch (err) {
      toast({
        title: t("auth.verify.errorTitle"),
        description: getLocalizedAuthErrorMessage(err, t),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (resendCooldown > 0 || !targetEmail) return;

    setLoading(true);
    try {
      await resendVerificationCodeHandler(targetEmail);
      setResendCooldown(60);
      setCode("");
      setError("");
      toast({
        title: ts("auth.verify.resendCodeSuccess"),
        status: "success",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } catch (err) {
      toast({
        title: ts("auth.verify.resendCodeError"),
        description: getLocalizedAuthErrorMessage(err, t),
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Center position="relative" minH="100vh" overflow="hidden">
      <Box
        position="fixed"
        top={0}
        left={0}
        zIndex={-1}
        h="100vh"
        w="100vw"
        bgGradient={lightGradient}
        _dark={{ bgGradient: darkGradient }}
        bg={pageBg}
      />
      <MotionBox
        maxW={{ base: "90%", md: "400px", "2xl": "450px" }}
        px={{ base: "24px", md: "40px" }}
        w="full"
        boxShadow="md"
        borderRadius="md"
        py="44px"
        bg={bg}
        layout
        transition={authCardTransition}
      >
        <AuthLogo />

        <Center mb={6}>
          <Text fontSize="16px" fontWeight="medium" color={textColor}>
            {t("auth.verify.title")}
          </Text>
        </Center>

        <form onSubmit={handleSubmit} autoComplete="off">
          <VStack spacing={{ base: "24px", md: "32px" }} align="stretch">
            <FormControl isInvalid={!!error}>
              <Center>
                <VStack alignItems="start">
                  <FormLabel>{t("auth.verify.codeLabel")}</FormLabel>
                  <HStack>
                    <PinInput
                      otp
                      size={{ base: "md", md: "lg" }}
                      focusBorderColor="primary.500"
                      onChange={(value) => setCode(value)}
                      value={code}
                    >
                      <PinInputField />
                      <PinInputField />
                      <PinInputField />
                      <PinInputField />
                      <PinInputField />
                      <PinInputField />
                    </PinInput>
                  </HStack>
                </VStack>
              </Center>
              <FormErrorMessage color={errorColor}>{error}</FormErrorMessage>
            </FormControl>

            <FormErrorBox errors={error ? [error] : []} />

            <CustomButton type="submit" isDisabled={loading || isLoading}>
              {loading || isLoading ? (
                <Spinner size="sm" />
              ) : (
                t("auth.verify.submit")
              )}
            </CustomButton>

            <Center>
              <Button
                variant="ghost"
                size="sm"
                fontSize="14px"
                color={resendCooldown > 0 ? "gray.400" : linkColor}
                isDisabled={resendCooldown > 0 || loading || isLoading}
                onClick={handleResendCode}
                _hover={{ textDecor: "underline", bg: "transparent" }}
              >
                {resendCooldown > 0
                  ? `${ts("auth.verify.resendCode")} (${resendCooldown}s)`
                  : ts("auth.verify.resendCode")}
              </Button>
            </Center>

            <Center>
              <Text
                fontSize="14px"
                color={linkColor}
                textDecor="underline"
                cursor="pointer"
                onClick={() => navigate("/login")}
              >
                {t("auth.verify.backToLogin")}
              </Text>
            </Center>
          </VStack>
        </form>
      </MotionBox>
    </Center>
  );
};

export default VerifyCodePage;
